import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Button,
  Form as PForm,
  FormLayout,
  Icon,
  Modal,
  SkeletonBodyText,
  Stack,
} from "@shopify/polaris"
import {
  CirclePlusMajor,
  CirclePlusMinor,
  EditMinor,
} from "@shopify/polaris-icons"
import {
  ExternalLabel,
  Select,
  TextField,
} from "components/widgets/CollectPage/forms/fields/CollectFields"
import { useTranslation } from "react-i18next"
import { useFormContext } from "../../FormContext"
import { useCollectContext } from "../../../CollectContextProvider"
import { LocalCommunityInitiativesEntity } from "services/entities/StandardEntity"
import { useEntity } from "hooks/useAxios"

const modalTranslationPath =
  "1_2_11.positive_impact.local_community_initiative.modal"
const translationPath =
  "collect:questions.1_2_11.positive_impact.local_community_initiative"

const LocalCommunityInitiative = () => {
  const { isCollectLocked } = useCollectContext()
  const [shouldLoad, setShouldLoad] = useState(true)
  const [localCommunityInitiatives, setLocalCommunityInitiatives] = useState(
    null
  )
  const { t } = useTranslation()
  const [isModalOpen, setModalOpen] = useState(false)
  const isMounted = useRef(null)
  const [isSending, setIsSending] = useState(false)
  const localCommunityInitiativesEntity = useEntity(
    LocalCommunityInitiativesEntity
  )

  const {
    formData,
    setFormData,
    setErrors,
    setTranslationPath,
  } = useFormContext()

  useEffect(() => setTranslationPath(modalTranslationPath), [])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (!shouldLoad) return
    setShouldLoad(false)
    localCommunityInitiativesEntity.list().then((response) => {
      if (!isMounted.current) return
      setLocalCommunityInitiatives(response.data)
    })
  }, [shouldLoad])

  const toggleModalOpen = useCallback(() => {
    if (isModalOpen) {
      setErrors({})
      setFormData({})
      setShouldLoad(true)
    }
    setModalOpen(!isModalOpen)
  }, [isModalOpen])

  const saveInitiative = () => {
    const successCallback = () => {
      setIsSending(false)
      setShouldLoad(true)
      toggleModalOpen()
    }
    const errorCallback = (error) => {
      setIsSending(false)
      setErrors(error.response.data)
    }
    const data = new FormData()
    if (formData.initiative_type) {
      data.set(
        "local_community_initiative[initiative_type]",
        formData.initiative_type
      )
    }
    if (formData.budget) {
      data.set("local_community_initiative[budget]", formData.budget)
    }
    if (formData.staff_involved_rate) {
      data.set(
        "local_community_initiative[staff_involved_rate]",
        formData.staff_involved_rate
      )
    }
    setErrors({})
    setIsSending(true)
    const promise = formData.id
      ? localCommunityInitiativesEntity.update(formData.id, data)
      : localCommunityInitiativesEntity.create(data)
    promise.then(successCallback).catch(errorCallback)
  }

  const deleteInitiative = useCallback(() => {
    if (!formData.id) return
    setIsSending(true)
    localCommunityInitiativesEntity
      .delete(formData.id)
      .then(() => {
        setIsSending(false)
        setShouldLoad(true)
        toggleModalOpen()
      })
      .catch(() => {
        setIsSending(false)
      })
  }, [formData])

  const modalMarkup = () => {
    const title = formData.id
      ? t(translationPath + ".modal.edit")
      : t(translationPath + ".modal.add")
    const secondaryAction = formData.id
      ? {
          content: t("common:delete"),
          onAction: deleteInitiative,
          destructive: true,
          loading: isSending,
        }
      : null

    return (
      <Modal
        open={isModalOpen}
        onClose={toggleModalOpen}
        title={title}
        primaryAction={{
          content: t("common:save"),
          onAction: saveInitiative,
          loading: isSending,
        }}
        secondaryActions={secondaryAction}
      >
        <div className="social">
          <Modal.Section>
            <PForm onSubmit={saveInitiative} implicitSubmit={false}>
              <FormLayout>
                <Select
                  id="initiative_type"
                  options={[
                    {
                      label: t(translationPath + ".modal.options.poverty"),
                      value: "poverty",
                    },
                    {
                      label: t(translationPath + ".modal.options.hunger"),
                      value: "hunger",
                    },
                    {
                      label: t(translationPath + ".modal.options.education"),
                      value: "education",
                    },
                    {
                      label: t(translationPath + ".modal.options.other"),
                      value: "other",
                    },
                  ]}
                />
                <TextField id="budget" type="number" />
                <TextField id="staff_involved_rate" type="number" />
              </FormLayout>
            </PForm>
          </Modal.Section>
        </div>
      </Modal>
    )
  }

  const tableMarkup = useCallback(() => {
    if (localCommunityInitiatives == null) return <SkeletonBodyText />
    const items = []
    if (localCommunityInitiatives.length === 0) {
      items.push(
        <tr key="0">
          <td colSpan={5}>
            <Stack
              distribution="center"
              alignment="center"
              spacing="extraTight"
            >
              <p>{t(translationPath + ".empty.label")}</p>
              {isCollectLocked ? null : (
                <Button plain onClick={toggleModalOpen}>
                  <Icon source={CirclePlusMajor} />
                </Button>
              )}
            </Stack>
          </td>
        </tr>
      )
    } else {
      localCommunityInitiatives.forEach((initiative) => {
        items.push(
          <tr key={initiative.id}>
            <td>
              {t(
                translationPath + ".modal.options." + initiative.initiative_type
              )}
            </td>
            <td>
              {" "}
              {initiative.budget} {t(translationPath + ".modal.budget.suffix")}
            </td>
            <td>
              {" "}
              {initiative.staff_involved_rate}{" "}
              {t(translationPath + ".modal.staff_involved_rate.suffix")}{" "}
            </td>
            {isCollectLocked ? null : (
              <td>
                <Button
                  plain
                  onClick={() => {
                    setFormData(initiative)
                    toggleModalOpen()
                  }}
                >
                  <Icon source={EditMinor} />
                </Button>
              </td>
            )}
          </tr>
        )
      })
    }
    return (
      <div>
        <ExternalLabel
          pathOverride="1_2_11.positive_impact"
          translationPathName="local_community_initiative.title"
        />
        <Stack vertical>
          <table className="Verdikt-DynamicTable">
            <thead>
              <tr className="leftAligned">
                <th>{t(translationPath + ".type.label")}</th>
                <th>{t(translationPath + ".budget.label")}</th>
                <th>{t(translationPath + ".staff_involved_rate.label")}</th>
                {isCollectLocked ? null : (
                  <th>
                    <Button plain onClick={toggleModalOpen}>
                      <Icon source={CirclePlusMinor} />
                    </Button>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>{items}</tbody>
          </table>
        </Stack>
      </div>
    )
  }, [localCommunityInitiatives])

  return (
    <div>
      {modalMarkup()}
      {tableMarkup()}
    </div>
  )
}

export default LocalCommunityInitiative
